import React, { useRef, useState, useMemo, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import FullCalendar from "@fullcalendar/react";
import timeGridPlugin from "@fullcalendar/timegrid";
import dayGridPlugin from "@fullcalendar/daygrid";
import interactionPlugin from "@fullcalendar/interaction";
import ChartLoading from "../../insight/components/ChartLoading";
import AIContentAction from "../../actions/AIContentAction";
import { selectSocialPosts } from "../selectors/aiContentSelectors";
import { addHours, endOfDay, isSameDay } from "date-fns";
import { localDateTimeInTimeZone } from "../helpers";

const calendarStyles = `
  .fc-theme-standard .fc-col-header-cell {
    background-color: #F8FAFC;
    padding: 10px !important;
  }
  .fc-theme-standard .fc-col-header-cell-cushion {
    color: #6B7280;
    font-weight: 600;
    text-transform: uppercase;
  }
  @keyframes monthChangeLabel {
    from { opacity: 0; transform: translateY(-6px) scale(0.98); }
    to { opacity: 1; transform: translateY(0) scale(1); }
  }
  .month-change-animate {
    animation: monthChangeLabel 320ms ease-out;
  }
  @keyframes monthGridPulse {
    0% { box-shadow: 0 0 0 0 rgba(37,99,235,0.15); }
    100% { box-shadow: 0 0 0 12px rgba(37,99,235,0); }
  }
  .calendar-month-change {
    animation: monthGridPulse 480ms ease-out;
    border-radius: 12px;
  }
`;

interface CalendarViewProps {
  socialPosts: any[];
  isGenerating: boolean;
  isFullCalendarView: boolean;
  onDateSelect: (selectionInfo: any) => void;
  onEventDrop: (info: any) => void;
  onEventResize: (info: any) => void;
  onEventClick: (info: any) => void;
  renderEventContent: (arg: any) => React.ReactNode;
  initialView?: string;
  calendarRef: React.RefObject<any>;
  eventsCount: number;
  onClearPosts: () => void;
  onToggleFullView: () => void;
  onHeightChange?: (height: number) => void;
  onDatesSet?: (arg: any) => void;
  platformFilterValue?: string;
  statusFilterValue?: string;
  timezoneValue?: string;
  onPlatformFilterChange?: (value: string) => void;
  onStatusFilterChange?: (value: string) => void;
  onTimezoneChange?: (value: string) => void;
  monthAnimationKey?: number; // change to trigger month animation explicitly
}

const CalendarView: React.FC<CalendarViewProps> = ({
  isGenerating,
  isFullCalendarView,
  renderEventContent,
  initialView = "dayGridMonth",
  calendarRef,
  onHeightChange,
  onDatesSet,
  monthAnimationKey,
}) => {
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();
  const currentDate = useSelector(
    (state: any) => state.aiContent?.get?.("currentDate") || new Date()
  );
  const statusFilter = useSelector((state: any) => state.aiContent?.get?.("statusFilter") || "All");
  const timezoneFilter = useSelector(
    (state: any) => state.aiContent?.get?.("timezoneFilter") || "All"
  );
  const platformFilter = useSelector(
    (state: any) => state.aiContent?.get?.("platformFilter") || "All"
  );
  const reduxSocialPosts = useSelector(selectSocialPosts);

  const prevEventsFingerprintRef = useRef<string>("__init__");
  const prevEventsLengthRef = useRef(0);
  const calendarRootRef = useRef<HTMLDivElement>(null);

  const postsFingerprint = (posts: any[] = []) => {
    try {
      return posts
        .map(
          (p: any) =>
            `${p.id}:${p?.data?.status?.value ?? ""}:${
              p?.data?.scheduledTime?.value ??
              p?.data?.scheduledTimeUTC ??
              p?.data?.start?.value ??
              ""
            }`
        )
        .join("|");
    } catch {
      return `len:${posts?.length ?? 0}`;
    }
  };

  const eventsFingerprint = (evts: any[] = []) => {
    try {
      return evts
        .map(
          (e: any) =>
            `${e.id}:${e.start?.getTime?.() ?? ""}:${e.end?.getTime?.() ?? ""}:${
              e.extendedProps?.status ?? ""
            }`
        )
        .join("|");
    } catch {
      return `len:${evts?.length ?? 0}`;
    }
  };

  const [animateMonthGrid, setAnimateMonthGrid] = useState(false);

  // Transform socialPosts to FullCalendar events with platform filtering
  const events = useMemo(() => {
    if (!reduxSocialPosts || reduxSocialPosts.length === 0) {
      return [];
    }

    // Filter posts by platform first
    const filteredByPlatform = reduxSocialPosts.filter((post) => {
      const currentFilter = String(platformFilter || "All");
      if (currentFilter === "All") return true;
      const platform = post.data?.platform?.value || "instagram";
      return platform.toLowerCase() === currentFilter.toLowerCase();
    });

    // Then filter by status if needed
    const filteredByStatus = filteredByPlatform.filter((post) => {
      const currentStatus = String(statusFilter || "All");
      if (currentStatus === "All") return true;
      const status = (post.data?.status?.value || "").toString().toLowerCase();
      return status === currentStatus.toLowerCase();
    });

    // Then filter by timezone if needed
    const filteredPosts = filteredByStatus.filter((post) => {
      const currentTimezoneFilter = String(timezoneFilter || "All");
      if (currentTimezoneFilter === "All") return true;
      const postTimezone = post.data?.timezone?.value || "";
      return String(postTimezone) === currentTimezoneFilter;
    });

    const sortedPosts = filteredPosts.slice().sort((a, b) => {
      const getStart = (p: any) =>
        Number(
          p?.data?.scheduledTime?.value ?? p?.data?.scheduledTimeUTC ?? p?.data?.start?.value ?? 0
        ) || 0;
      const aTime = getStart(a);
      const bTime = getStart(b);
      if (aTime !== bTime) {
        return aTime - bTime;
      }
      const platformA = (a.data?.platform?.value || "").toString().toLowerCase();
      const platformB = (b.data?.platform?.value || "").toString().toLowerCase();
      if (platformA !== platformB) {
        return platformA.localeCompare(platformB);
      }
      const titleA = (a.data?.summary?.value || "").toString().toLowerCase();
      const titleB = (b.data?.summary?.value || "").toString().toLowerCase();
      return titleA.localeCompare(titleB);
    });

    const getEventTimezone = (post: any): string => {
      const postTz = post?.data?.timezone?.value;
      if (postTz && String(postTz) !== "All") return String(postTz);
      if (timezoneFilter && String(timezoneFilter) !== "All") return String(timezoneFilter);
      try {
        return String(Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC");
      } catch {
        return "UTC";
      }
    };

    const computedEvents = sortedPosts
      .map((post) => {
        const data = post.data || post;
        const startDate =
          data.scheduledTime?.value ??
          data.start?.value ??
          data.scheduledTimeUTC ??
          data.startTime?.value;

        if (!startDate) {
          return null;
        }

        const tz = getEventTimezone(post);

        const startDateObj = (() => {
          try {
            if (Array.isArray(startDate) && startDate.length >= 3) {
              const [year, month, day, hour = 9, minute = 15] = startDate as number[];
              const base = new Date(
                year,
                (month as number) - 1,
                day as number,
                hour as number,
                minute as number,
                0
              );
              return localDateTimeInTimeZone(base, tz);
            }
            const parsed = new Date(startDate);
            if (isNaN(parsed.getTime())) return null;
            return localDateTimeInTimeZone(parsed, tz);
          } catch {
            return null;
          }
        })();

        if (!startDateObj) {
          return null;
        }

        const _endCandidate = addHours(startDateObj, 1);
        const endDateObj = isSameDay(startDateObj, _endCandidate)
          ? _endCandidate
          : endOfDay(startDateObj);

        const stableId =
          post.id ||
          data?.id ||
          `${data.platform?.value || "platform"}-${startDateObj.getTime()}-${
            data.summary?.value || Math.random()
          }`;

        return {
          ...post,
          title: data.summary?.value || "",
          start: startDateObj,
          end: endDateObj,
          id: String(stableId),
          allDay: false,
          extendedProps: {
            ...data,
            platform: data.platform?.value || "instagram",
            status: data.status?.value || "unscheduled",
            description: data.description?.value || "",
            topic: data.topic?.value || "",
            time: startDateObj.toISOString(),
          },
        };
      })
      .filter((event) => event !== null);

    return computedEvents;
  }, [reduxSocialPosts, platformFilter, statusFilter, timezoneFilter]);

  const handleDatesSet = (arg: any) => {
    const view = arg?.view;
    const visibleStart: Date | undefined = view?.activeStart || arg?.start;
    const visibleEnd: Date | undefined = view?.activeEnd || arg?.end;
    const nextDate = view?.currentStart || visibleStart || arg?.start;

    // Defer state updates out of FullCalendar's rendering cycle to avoid
    // "flushSync called inside a lifecycle method" warning in React 18
    setTimeout(() => {
      dispatch(AIContentAction.setCurrentDate(nextDate) as any);

      if (onDatesSet) {
        onDatesSet({ ...arg, start: visibleStart, end: visibleEnd });
      }
    }, 0);
  };

  const eventsKey = useMemo(() => eventsFingerprint(events as any), [events]);
  const postsKey = useMemo(() => postsFingerprint(reduxSocialPosts || []), [reduxSocialPosts]);
  const prevPostsKeyRef = useRef<string>("__init_posts__");

  // eslint-disable-next-line react-hooks/exhaustive-deps -- reloadCalendar used in effects on purpose
  const reloadCalendar = () => {
    // Defer all FullCalendar API calls out of React's rendering cycle.
    // calendarApi.render() can internally call datesSet → dispatch → flushSync,
    // which React 18 warns about when called during a render phase.
    setTimeout(() => {
      if (!calendarRef.current) return;
      const calendarApi = calendarRef.current.getApi();
      try {
        calendarApi.removeAllEventSources();
        calendarApi.removeAllEvents();
      } catch {}
      calendarApi.addEventSource(events);
      calendarApi.render();
      try {
        (calendarApi as any).getEventSources?.();
        calendarApi.getEvents?.();
      } catch {}
      // update refs
      prevEventsLengthRef.current = events.length;
      prevEventsFingerprintRef.current = eventsKey;
    }, 0);
  };

  // Force re-render FullCalendar when events change
  useEffect(() => {
    const currentEventsLength = events.length;
    const prevEventsLength = prevEventsLengthRef.current;
    const curFP = eventsKey;
    const prevFP = prevEventsFingerprintRef.current;
    const fingerprintChanged = prevFP !== curFP;

    if (currentEventsLength !== prevEventsLength || fingerprintChanged) {
      reloadCalendar();
    }
  }, [events.length, eventsKey, events, reloadCalendar]);

  // Also reload when raw posts fingerprint changes (cover cases where events array identity didn’t change)
  useEffect(() => {
    if (!calendarRef.current) return;
    const prev = prevPostsKeyRef.current;
    const cur = postsKey;
    if (prev !== cur) {
      reloadCalendar();
      prevPostsKeyRef.current = cur;
    }
  }, [postsKey, calendarRef, reloadCalendar]);

  // Measure and report height changes with debounce
  useEffect(() => {
    if (calendarRootRef.current && onHeightChange) {
      const timeoutId = setTimeout(() => {
        const height = calendarRootRef.current?.offsetHeight;
        if (height && height > 0) {
          onHeightChange(height);
        }
      }, 100); // 100ms debounce

      return () => clearTimeout(timeoutId);
    }
  }, [events.length, isFullCalendarView]); // eslint-disable-line react-hooks/exhaustive-deps -- onHeightChange omitted to avoid parent re-render loop

  // Get locale for FullCalendar
  const getCalendarLocale = () => {
    const currentLang = i18n.language || "en";
    return currentLang.startsWith("vi") ? "vi" : "en";
  };

  // Animate only when explicitly requested (e.g., after Generate success)
  useEffect(() => {
    if (monthAnimationKey && monthAnimationKey > 0) {
      setAnimateMonthGrid(true);
      const gridTimeout = setTimeout(() => setAnimateMonthGrid(false), 520);
      return () => {
        clearTimeout(gridTimeout);
      };
    }
  }, [monthAnimationKey]);

  return (
    <div className="w-full flex-col" ref={calendarRootRef}>
      <style>{calendarStyles}</style>

      {/* Calendar */}
      <div
        className={`bg-white rounded-xl relative ${isFullCalendarView ? "w-full" : "flex-1"} ${
          animateMonthGrid ? "calendar-month-change" : ""
        }`}
      >
        {isGenerating && (
          <div className="absolute inset-0 bg-white/80 backdrop-blur-sm z-2 flex items-center justify-center rounded">
            <ChartLoading label={t("Loading calendar...")}></ChartLoading>
          </div>
        )}
        <FullCalendar
          ref={calendarRef}
          plugins={[timeGridPlugin, dayGridPlugin, interactionPlugin]}
          initialView={initialView as any}
          initialDate={currentDate}
          locale={getCalendarLocale()}
          firstDay={1}
          stickyHeaderDates={false}
          headerToolbar={false}
          showNonCurrentDates={true}
          fixedWeekCount={true}
          selectable={false}
          selectMirror={false}
          editable={false}
          eventStartEditable={false}
          eventDurationEditable={false}
          droppable={false}
          // events are controlled via API effect to avoid stale duplicates
          // events={events}
          // Disabled interactions

          select={undefined}
          eventDrop={undefined}
          eventResize={undefined}
          eventContent={renderEventContent}
          eventClick={undefined}
          eventAllow={() => false}
          datesSet={handleDatesSet}
          timeZone={"local"}
          eventDisplay="block"
          eventDidMount={(info) => {
            const el = info.el as HTMLElement;
            el.style.background = "transparent";
            el.style.border = "none";
            el.style.padding = "0";
            el.style.boxShadow = "none";
            el.style.outline = "none";
            // Allow multi-line content in dayGrid cells
            el.style.whiteSpace = "normal";
            const main = el.querySelector(".fc-event-main") as HTMLElement | null;
            if (main) {
              main.style.whiteSpace = "normal";
              main.style.overflow = "visible";
            }
          }}
          dayCellDidMount={(info) => {
            const el = info.el as HTMLElement;
            el.style.backgroundColor = "white";
          }}
          height={isFullCalendarView ? "calc(100vh - 160px)" : "auto"}
          expandRows
          dayMaxEvents={false}
          dayMaxEventRows={false}
          nowIndicator
          slotMinTime="06:00:00"
          slotMaxTime="22:00:00"
          allDaySlot={false}
          eventTimeFormat={{
            hour: "2-digit",
            minute: "2-digit",
            meridiem: "short",
          }}
        />
      </div>
    </div>
  );
};

export default CalendarView;
